import useSWR from "swr"
import { api_fetch_throws } from "@/api/client"
import { CalendarInfo } from "./types"

/** `GET /promotions/account-options`: the org's known customers (distributors) and retailers. */
export interface PromotionAccountOptions {
  customers: string[]
  retailers: string[]
}

/**
 * Existing customer names to choose from when creating a promotion (existing promotions'
 * customers plus calendar retailer names plus "Direct"), so free-text typos like a missing
 * apostrophe can't create near-duplicate customers that silently break deduction matching.
 */
export function existing_customer_options(calendars: CalendarInfo[], current_value?: string): string[] {
  const names = new Set<string>()
  for (const calendar of calendars) {
    if (calendar.retailer_name) names.add(calendar.retailer_name)
    if (calendar.customer_name) names.add(calendar.customer_name)
    for (const promotion of calendar.promotions) {
      if (promotion.customer) names.add(promotion.customer)
    }
  }
  names.add("Direct")
  // Keep the currently selected value in the options so the select can display it.
  if (current_value) names.add(current_value)
  return Array.from(names).sort((a, b) => a.localeCompare(b))
}

/** Retailer names already in use: each calendar's retailer plus its loaded promotions' retailers. */
export function existing_retailer_options(calendars: CalendarInfo[], current_value?: string): string[] {
  const names = new Set<string>()
  for (const calendar of calendars) {
    if (calendar.retailer_name) names.add(calendar.retailer_name)
    for (const promotion of calendar.promotions) {
      if (promotion.retailer) names.add(promotion.retailer)
    }
  }
  // Keep the currently selected value in the options so the select can display it.
  if (current_value) names.add(current_value)
  return Array.from(names).sort((a, b) => a.localeCompare(b))
}

/**
 * One sorted list from several lists of account names: trimmed, blanks dropped, and names that
 * differ only by case merged. The first spelling seen wins, so pass the list whose spelling must
 * survive first — the value on the form, which the select can only display when it is an option
 * exactly as stored.
 */
export function merge_account_names(...lists: Array<Array<string | null | undefined> | undefined>): string[] {
  const by_key = new Map<string, string>()
  for (const list of lists) {
    for (const name of list ?? []) {
      const trimmed = name?.trim()
      if (!trimmed) continue
      const key = trimmed.toLowerCase()
      if (!by_key.has(key)) by_key.set(key, trimmed)
    }
  }
  return Array.from(by_key.values()).sort((a: string, b: string) => a.localeCompare(b))
}

/** Customer value for a promotion sold straight to the retailer, with no distributor in between. */
export const DIRECT_CUSTOMER = "Direct"

/**
 * PURE: the Customer (Distributor) dropdown's options — "Direct" pinned first with a label that
 * says what it means, then every other known customer. Tyler (1st Phorm) asked how to enter a promo
 * for a retailer that buys direct; "Direct" was in the list but sorted among the distributors.
 */
export function customer_select_options(names: string[]): Array<{ value: string; label: string }> {
  const others = names.filter((name: string) => name !== DIRECT_CUSTOMER)
  return [
    { value: DIRECT_CUSTOMER, label: "Direct (no distributor, the retailer buys from you)" },
    ...others.map((name: string) => ({ value: name, label: name })),
  ]
}

async function fetch_promotion_account_options(): Promise<PromotionAccountOptions> {
  return api_fetch_throws<PromotionAccountOptions>("/promotions/account-options")
}

/**
 * The org's customers and retailers for the promotion forms. Asked of the server rather than read
 * off the calendars in memory: promotions load a calendar at a time, so a list built from them was
 * missing every distributor whose calendar had not loaded yet, and a new calendar had none.
 */
export function use_promotion_account_options(): PromotionAccountOptions | undefined {
  const { data } = useSWR("/promotions/account-options", fetch_promotion_account_options)
  return data
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function calendar(overrides: Partial<CalendarInfo>): CalendarInfo {
    return {
      id: "cal-1",
      calendar_name: "Test Calendar",
      source_type: "manual",
      promotions: [],
      created_at: "2026-01-01T00:00:00Z",
      ...overrides,
    }
  }

  describe("existing_customer_options", () => {
    it("collects unique customers from promotions and calendar retailers", () => {
      const calendars = [
        calendar({
          id: "cal-1",
          retailer_name: "Bozzuto's",
          promotions: [
            { name: "P1", products: [], customer: "KeHE" },
            { name: "P2", products: [], customer: "Bozzuto's" },
          ],
        }),
        calendar({ id: "cal-2", promotions: [{ name: "P3", products: [], customer: "UNFI" }] }),
      ]
      expect(existing_customer_options(calendars)).toEqual(["Bozzuto's", "Direct", "KeHE", "UNFI"])
    })

    it("keeps the current value selectable even if it is not in the data", () => {
      expect(existing_customer_options([], "Legacy Customer")).toEqual(["Direct", "Legacy Customer"])
    })
  })

  describe("existing_retailer_options", () => {
    it("collects calendar retailers and promotion retailers, without inventing Direct", () => {
      const calendars = [
        calendar({
          retailer_name: "Safeway",
          promotions: [{ name: "P1", products: [], customer: "KeHE", retailer: "Safeway" }],
        }),
        calendar({ id: "cal-2", promotions: [{ name: "P2", products: [], customer: "UNFI", retailer: "Fareway" }] }),
      ]
      expect(existing_retailer_options(calendars)).toEqual(["Fareway", "Safeway"])
    })

    it("lists a brand-new calendar's own retailer and the value typed on the form", () => {
      expect(existing_retailer_options([calendar({ retailer_name: "test" })], "New Chain")).toEqual(["New Chain", "test"])
    })
  })

  describe("customer_select_options", () => {
    it("pins Direct first, explained, and lists it once", () => {
      expect(customer_select_options(["Core-Mark", "Direct", "KeHE"])).toEqual([
        { value: "Direct", label: "Direct (no distributor, the retailer buys from you)" },
        { value: "Core-Mark", label: "Core-Mark" },
        { value: "KeHE", label: "KeHE" },
      ])
    })

    it("offers Direct even when no promotion uses it yet", () => {
      expect(customer_select_options([])[0].value).toBe("Direct")
    })
  })

  describe("merge_account_names", () => {
    it("merges lists, dropping blanks and names that differ only by case", () => {
      expect(merge_account_names(["KeHE", " Direct "], ["kehe", "", undefined, "Amcon Distributing"], undefined)).toEqual([
        "Amcon Distributing",
        "Direct",
        "KeHE",
      ])
    })

    it("keeps the spelling of the list passed first", () => {
      expect(merge_account_names(["Dot foods"], ["Dot Foods", "DOT FOODS"])).toEqual(["Dot foods"])
    })

    it("still lists the server's customers when nothing has loaded locally", () => {
      expect(merge_account_names([], ["Core-Mark", "7-Eleven"])).toEqual(["7-Eleven", "Core-Mark"])
    })
  })
}
